/* eslint-disable camelcase */
import { useMemo } from 'react';
import {
  DateTime,
} from 'luxon';
import Autolinker from 'autolinker';

import { User } from '@gymcloud/core'

import {
  Avatar,
  VideoFrame,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import * as S from '../styled';

const getTime = (isoDate: string) => DateTime.fromISO(isoDate).toFormat('LL/dd/yyyy hh:mm a');
function matchYoutubeUrl(url: string) {
  const regExp = /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  const match = url.match(regExp);
  return match ? match[1] : false;
}

interface Props {
  user: User
  message: any
  nextMessage: any
}

const Message = ({ message, nextMessage, user }: Props) => {
  const sameSender = message.sender_id === nextMessage?.sender_id;
  const margin = sameSender ? '0 0 5px 0' : '0 0 30px 0';
  const { linkedBody, urlMatches } = useMemo(() => ({
    linkedBody: {
      __html: Autolinker.link(message.body || ''),
    },
    urlMatches: Autolinker
      .parse(message.body || '')
      .filter(m => matchYoutubeUrl(m.getUrl())),
  }), []);

  const isCurrentUser = message.sender_id === user.id

  const body = (
    <S.Body isCurrentUser={isCurrentUser}>
      {message.attachment?.url && (
        <img
          src={message.attachment.url}
          alt="attachment"
        />
      )}

      {(message.video_id || message.video_url) && (
        <VideoFrame
          src={message.video_url}
          title={message.body}
          width="400px"
          height="250px"
          allowFullScreen
        />
      )}

      <Flex column>
        {urlMatches.map((match, index) => {
          const originUrl = match.getUrl();
          const url = `https://www.youtube.com/embed/${matchYoutubeUrl(originUrl)}`;

          return (
            <VideoFrame
              key={`${url}+${index.toString()}`}
              src={url}
              title={message.body}
              width="400px"
              height="250px"
              allowFullScreen
            />
          );
        })}
      </Flex>

      <Text
        whiteSpace="pre-wrap"
        wordBreak="break-word"
      >
        <span dangerouslySetInnerHTML={linkedBody} />
      </Text>

      {message.workout_name && (
        <S.Workout>
          {message.workout_name}
        </S.Workout>
      )}
    </S.Body>
  );

  return isCurrentUser ? (
    <Flex
      width="100%"
      align="flex-end"
      shrink="0"
      justify="flex-end"
      spacing={15}
      margin={margin}
    >
      <Flex
        column
        maxWidth="70%"
        align="flex-end"
      >
        {body}
        {!sameSender && (
          <Flex
            width="100%"
            justify="flex-end"
          >
            <Text
              size="12px"
            >
              {getTime(message.created_at)}
            </Text>
          </Flex>
        )}
      </Flex>
      {sameSender ? (
        <Flex width="28px" height="28px" />
      ) : (
        <Avatar
          width="28px"
          height="28px"
          src={message.sender_avatar?.url || user.user_profile.avatar.thumb?.url}
        />
      )}
    </Flex>
  ) : (
    <Flex
      width="100%"
      align="flex-end"
      shrink="0"
      spacing={15}
      margin={margin}
    >
      {sameSender ? (
        <Flex width="28px" height="28px" />
      ) : (
        <Avatar
          width="28px"
          height="28px"
          src={message.sender_avatar?.url}
        />
      )}
      <Flex
        column
        maxWidth="70%"
      >
        {body}
        {!sameSender && (
          <Text
            size="12px"
          >
            {getTime(message.created_at)}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default Message;
